import { Component, ElementRef, EventEmitter, Input, Output, QueryList, ViewChildren, ChangeDetectionStrategy } from '@angular/core';

import { BlockReasonCode, CourseSessionDto, isSessionJoinable } from '../../../service/court-availability-service';
import { JoinPolicy } from '../../../features/admin/models/course.interface';
import { VenueTimezoneService } from '../../../service/venue-timezone.service';
import { segmentEventLabel } from '../court-grid-view/court-segment-label.util';

/**
 * Selectable course cards (radio-style) for the Einfache Ansicht "Platz
 * buchen" card. Purely presentational — selecting a card only emits
 * `sessionSelected`; the container flips the booking mode to 'course' and the
 * reserve CTA triggers the actual enrollment.
 */
@Component({
  selector: 'app-course-picker',
  standalone: true,
  imports: [],
  templateUrl: './course-picker.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./course-picker.component.css']
})
export class CoursePickerComponent {
  @Input() courseSessions: CourseSessionDto[] = [];
  @Input() selectedSessionId: string | null = null;
  @Input() loading = false;

  @Output() sessionSelected = new EventEmitter<CourseSessionDto>();

  @ViewChildren('courseRadio') private radioButtons!: QueryList<ElementRef<HTMLButtonElement>>;

  private readonly priceFormatter = new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
  });

  constructor(private venueTimezone: VenueTimezoneService) {}

  /**
   * Badge for one card, from the ONE §6.4.3 label table
   * (`court-segment-label.util`) — the same `kindLabel` pattern the enrolment
   * modal uses. "Kurs" was hardcoded, so a Spieltag card read "Kurs" next to
   * "vs. TC Musterstadt" (§7.3). No `displayName` is passed: the card already
   * renders the name beside the badge, and the label would stutter.
   */
  kindLabel(session: CourseSessionDto): string {
    // `kind` is passed through rather than hardcoded to 'course': a block must
    // never be able to read "Kurs" here. Class-A filtering keeps blocks out of
    // this payload today, so this is the fail-closed backstop, not a live case.
    return segmentEventLabel(session.kind, session.eventType);
  }

  selectSession(session: CourseSessionDto): void {
    if (this.isSelectable(session)) {
      this.sessionSelected.emit(session);
    }
  }

  /**
   * Roving tabindex for the radio group: the checked card is the single tab
   * stop; without a selection, the first selectable card takes that role.
   * Disabled (unavailable) cards are never focusable.
   */
  tabIndexFor(session: CourseSessionDto): number {
    if (!this.isSelectable(session)) return -1;
    const tabStop = this.courseSessions.find(
      s => this.isSelectable(s) && this.isSelected(s)
    ) ?? this.courseSessions.find(s => this.isSelectable(s));
    return tabStop?.id === session.id ? 0 : -1;
  }

  /** Arrow keys move selection between selectable cards (ARIA radio pattern). */
  onRadioKeydown(event: KeyboardEvent): void {
    const forward = event.key === 'ArrowDown' || event.key === 'ArrowRight';
    const backward = event.key === 'ArrowUp' || event.key === 'ArrowLeft';
    if (!forward && !backward) return;
    event.preventDefault();

    const selectable = this.courseSessions.filter(s => this.isSelectable(s));
    if (!selectable.length) return;

    const currentIndex = selectable.findIndex(s => this.isSelected(s));
    const next = currentIndex === -1
      ? selectable[forward ? 0 : selectable.length - 1]
      : selectable[(currentIndex + (forward ? 1 : -1) + selectable.length) % selectable.length];

    this.sessionSelected.emit(next);
    this.focusSession(next);
  }

  /** Moves DOM focus to a session's radio button (follows selection on arrow keys). */
  private focusSession(session: CourseSessionDto): void {
    const index = this.courseSessions.findIndex(s => s.id === session.id);
    // Defer one tick so the parent's selectedSessionId round-trip has applied.
    setTimeout(() => this.radioButtons?.get(index)?.nativeElement.focus());
  }

  isSelected(session: CourseSessionDto): boolean {
    return session.id === this.selectedSessionId;
  }

  /**
   * Only joinable, not-enrolled sessions are selectable. The rule itself lives
   * in `isSessionJoinable` (court-availability-service) — this card and the
   * grid tile must offer signup on exactly the same sessions, and they did not
   * while each carried its own copy.
   */
  isSelectable(session: CourseSessionDto): boolean {
    return isSessionJoinable(session);
  }

  /**
   * Meta line: "Kurs · 20:00 – 21:30 · Platz 2" (venue timezone) — with the
   * SAME §6.4.3 word the badge uses, so a Spieltag card does not say
   * "Spieltag" on the badge and "Kurs ·" one line below it. (§7.3's inventory
   * lists only the badge for this file; the meta line is the same literal.)
   */
  getMetaLine(session: CourseSessionDto): string {
    const start = this.venueTimezone.formatTime(session.startTimeUtc);
    const end = this.venueTimezone.formatTime(session.endTimeUtc);
    const courts = this.getCourtNames(session);
    const kind = this.kindLabel(session);
    const timeRange = `${start} – ${end}`;
    return courts ? `${kind} · ${timeRange} · ${courts}` : `${kind} · ${timeRange}`;
  }

  /** Green spots line: "8 Plätze übrig" / "1 Platz übrig". */
  getSpotsLabel(session: CourseSessionDto): string {
    const spots = session.availableSpots;
    return `${spots} ${spots === 1 ? 'Platz' : 'Plätze'} übrig`;
  }

  /** German status text for non-selectable sessions. */
  getStatusLabel(session: CourseSessionDto): string {
    if (session.isUserEnrolled) {
      return 'Bereits eingeschrieben';
    }
    if (session.isFull || session.availableSpots <= 0) {
      return 'Ausgebucht';
    }
    switch (session.joinRestriction) {
      case BlockReasonCode.COURSE_APPROVAL_REQUIRED:
        return this.getJoinPolicyLabel(JoinPolicy.APPROVAL_REQUIRED);
      case BlockReasonCode.COURSE_INVITE_ONLY:
      case BlockReasonCode.INVITE_ONLY:
        return this.getJoinPolicyLabel(JoinPolicy.INVITE_ONLY);
      case BlockReasonCode.COURSE_MEMBERS_ONLY:
      case BlockReasonCode.MEMBERS_ONLY:
        return this.getVisibilityLabel('members_only');
      default:
        return 'Nicht verfügbar';
    }
  }

  formatPrice(price?: number): string {
    return this.priceFormatter.format(price ?? 0);
  }

  getCourtNames(session: CourseSessionDto): string {
    return session.courts?.map(c => c.courtName).join(', ') || '';
  }

  /**
   * Get visibility label in German
   */
  getVisibilityLabel(visibility?: string): string {
    switch (visibility) {
      case 'public':
        return 'Öffentlich';
      case 'private':
        return 'Privat';
      case 'members_only':
        return 'Nur Mitglieder';
      default:
        return 'Öffentlich'; // Default to public if not specified
    }
  }

  /**
   * Get join policy label in German
   */
  getJoinPolicyLabel(joinPolicy?: JoinPolicy): string {
    switch (joinPolicy) {
      case JoinPolicy.OPEN:
        return 'Offen';
      case JoinPolicy.APPROVAL_REQUIRED:
        return 'Genehmigung erforderlich';
      case JoinPolicy.INVITE_ONLY:
        return 'Nur mit Einladung';
      default:
        return 'Offen';
    }
  }
}
